import React, { useState, useEffect } from 'react';
import AdminLayoutWrapper from '../../components/layout/AdminLayoutWrapper';
import DataTableLayout from '../../components/common/DataTableLayout';
import FilterPanel from '../../components/common/FilterPanel';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Tooltip,
  Chip,
  Alert,
  Snackbar,
  Switch,
  FormControlLabel
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import {
  GridToolbarColumnsButton,
  GridToolbarDensitySelector,
  GridToolbarExport,
} from '@mui/x-data-grid';
import { FEATURES } from '../../config/Config';
import { useNavigate } from 'react-router-dom';
import { createFilterChangeHandler, clearFilters, filterData, dataGridProps } from '../../utils/tableUtils';
import api from '../../services/api';

/**
 * Simple CRUD interface for `Feature` model.
 * It relies on backend endpoints:
 *   GET    /accounts/features/
 *   POST   /accounts/features/
 *   PATCH  /accounts/features/{id}/
 *   DELETE /accounts/features/{id}/
 * Adjust URL paths if your API differs.
 */

export default function FeatureManagementPage() {
  const [features, setFeatures] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ code: '', label: '', description: '' });
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null); // feature object or null
  const [form, setForm] = useState({ code: '', label: '', description: '' });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await api.get(FEATURES);
      const items = Array.isArray(res.data) ? res.data : (res.data?.results || []);
      setFeatures(items);
    } catch (err) {
      console.error('Error fetching features:', err);
      showSnackbar('Failed to load features', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleOpenForm = (feature = null) => {
    setEditing(feature);
    setForm(feature ? { ...feature } : { code: '', label: '', description: '' });
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setForm({ code: '', label: '', description: '' });
    setEditing(null);
  };

  const handleSubmit = async () => {
    if (!form.code || !form.label) {
      showSnackbar('Please fill in all required fields', 'error');
      return;
    }

    try {
      if (editing) {
        await api.patch(`${FEATURES}${editing.id}/`, form);
        showSnackbar('Feature updated successfully');
      } else {
        await api.post(FEATURES, form);
        showSnackbar('Feature created successfully');
      }
      handleClose();
      fetchData();
    } catch (err) {
      console.error('Error saving feature:', err);
      showSnackbar('Operation failed', 'error');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this feature?')) return;
    try {
      await api.delete(`${FEATURES}${id}/`);
      showSnackbar('Feature deleted successfully');
      fetchData();
    } catch (err) {
      console.error('Error deleting feature:', err);
      showSnackbar('Delete failed', 'error');
    }
  };

  const filteredFeatures = filterData(features, filters);

  const handleFilterChange = createFilterChangeHandler(setFilters);
  const handleClearFilters = () => clearFilters(setFilters, { code: '', label: '', description: '' });

  const filterConfig = [
    {
      field: 'code',
      label: 'Code',
      type: 'text',
      placeholder: 'Search by code...'
    },
    {
      field: 'label',
      label: 'Label',
      type: 'text',
      placeholder: 'Search by label...'
    },
    {
      field: 'description',
      label: 'Description',
      type: 'text',
      placeholder: 'Search by description...'
    }
  ];

  const columns = [
    {
      field: 'code',
      headerName: 'Code',
      flex: 1,
      renderCell: (params) => (
        <Chip 
          label={params.value} 
          size="small" 
          color="primary" 
          variant="outlined"
        />
      )
    },
    {
      field: 'label',
      headerName: 'Label',
      flex: 2
    },
    {
      field: 'description',
      headerName: 'Description',
      flex: 3
    },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 1,
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Edit">
            <IconButton
              size="small"
              onClick={() => handleOpenForm(params.row)}
              sx={{ p: 0.5 }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton
              size="small"
              onClick={() => handleDelete(params.row.id)}
              sx={{ p: 0.5, color: 'error.main' }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      )
    }
  ];

  const actionButtons = [
    {
      label: 'Add Feature',
      icon: <AddIcon />,
      variant: 'contained',
      onClick: () => handleOpenForm(null)
    }
  ];

  const emptyStateProps = {
    title: 'No features found',
    description: 'Try adjusting your filters or add your first feature.',
    icon: <AddIcon sx={{ fontSize: 40, color: 'text.secondary' }} />
  };

  return (
    <AdminLayoutWrapper>
      <DataTableLayout
        title="Features"
        actionButtons={actionButtons}
        filterPanel={
          <FilterPanel
            filters={filters}
            onFilterChange={handleFilterChange}
            onClearFilters={handleClearFilters}
            filterConfig={filterConfig}
          />
        }
        loading={loading}
        isEmpty={!loading && filteredFeatures.length === 0}
        emptyStateProps={emptyStateProps}
        mobileFilterOpen={mobileFilterOpen}
        onMobileFilterClose={() => setMobileFilterOpen(!mobileFilterOpen)}
      >
        <DataGrid
          rows={filteredFeatures}
          columns={columns}
          {...dataGridProps}
          components={{
            Toolbar: () => (
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                mb: 2,
                p: 1
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <GridToolbarColumnsButton 
                    sx={{ 
                      color: 'primary.main',
                      '&:hover': { backgroundColor: 'primary.50' }
                    }}
                  />
                  <GridToolbarDensitySelector 
                    sx={{ 
                      color: 'primary.main',
                      '&:hover': { backgroundColor: 'primary.50' }
                    }}
                  />
                  <GridToolbarExport 
                    sx={{ 
                      color: 'primary.main',
                      '&:hover': { backgroundColor: 'primary.50' }
                    }}
                  />
                </Box>
              </Box>
            )
          }}
        />
      </DataTableLayout>

      {/* Add/Edit Dialog */}
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editing ? 'Edit Feature' : 'Add Feature'}
        </DialogTitle>
        <DialogContent>
          <TextField
            label="Code"
            value={form.code}
            onChange={(e) => setForm({ ...form, code: e.target.value })}
            fullWidth
            margin="normal"
            required
            autoFocus
            placeholder="e.g., FEATURE_NAME"
          />
          <TextField
            label="Label"
            value={form.label}
            onChange={(e) => setForm({ ...form, label: e.target.value })}
            fullWidth
            margin="normal"
            required
            placeholder="e.g., Feature Display Name"
          />
          <TextField
            label="Description"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            fullWidth
            margin="normal"
            multiline
            rows={3}
            placeholder="Describe what this feature does..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editing ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </AdminLayoutWrapper>
  );
}
